import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { formatNumber } from "@/lib/mrp";

const SGS_LOCATION_ID = 1;
const SUN_LOCATION_ID = 2;
const WEEKDAY_ALLOTMENT_KEYS = ["mon", "tue", "wed", "thu", "fri"] as const;

interface SiteAllotmentRow {
  locationId: number;
  locationName: string;
  minutesByWeekday: Record<string, number>;
}

interface StaffRow {
  id: number;
  userId: string;
  locationId: number;
  locationName: string;
  active: boolean;
  displayName: string | null;
  userEmail: string | null;
  weekdayHours: number | null;
  percentAvailable: number | null;
}

interface UserOption {
  userId: string;
  email: string;
  displayName: string | null;
}

interface StationStandardRow {
  id: number;
  locationId: number;
  locationName: string | null;
  productFamily: string;
  stationKey: string;
  manMinutesPerUnit: number;
}

interface StationStandardsResponse {
  standards: StationStandardRow[];
  families: string[];
  stationKeysByLocation: Record<string, string[]>;
}

interface RefreshStandardsResponse {
  updated: number;
  skipped: Array<{ productFamily: string; stationKey: string; reason: string }>;
}

const PRODUCT_FAMILY_LABELS: Record<string, string> = {
  fi_3c: "FI-3C (non-Q)",
  fi_3c_q: "FI-3C-Q",
  fi_5a: "FI-5A",
  fi_5a_w: "FI-5A-W",
  assembly_magnet: "Assembly magnet kits",
  clamp_qc: "Clamp QC",
};

const SGS_DEVICE_STATION_KEYS = [
  "config",
  "cal+test",
  "pre-encapsulation",
  "final-qc",
  "pre-pack",
  "shipping-team",
] as const;

function stationKeysForFamilyEditor(locationId: string, productFamily: string): string[] {
  if (productFamily === "clamp_qc") return ["clamp_qc"];
  if (productFamily === "assembly_magnet") return ["assembly"];
  if (locationId === "2") return ["before_configuration", "after_configuration"];
  return [...SGS_DEVICE_STATION_KEYS];
}

const STATION_LABELS: Record<string, string> = {
  config: "Configure",
  "cal+test": "Calibrate + test",
  "pre-encapsulation": "Pre-encapsulation",
  "final-qc": "Final QC",
  "pre-pack": "Pre-pack",
  "shipping-team": "Shipping",
  before_configuration: "Before configuration",
  after_configuration: "After configuration",
  assembly: "Assembly",
  clamp_qc: "Clamp QC",
};

const SECTION_HELP =
  "SGS labor uses named hub users with weekly hours and percent available for production. Suntronic uses a fixed weekday man-minute budget (no individual roster). Station man-minute standards apply by product family and manufacturing station.";

const capacitySettingsAdminTitle =
  "Manufacturing management hub administrators can edit production capacity settings. Ask an administrator if you need to change staff, schedules, or station standards.";

function chicagoToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());
}

function rosterErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message !== "") return error.message;
  return "Could not update the SGS roster.";
}

function RosterScheduleEditor({
  row,
  disabled,
  pending,
  onSave,
}: {
  row: StaffRow;
  disabled: boolean;
  pending: boolean;
  onSave: (staffId: number, weekdayHours: number, percentAvailable: number) => void;
}) {
  const [hoursText, setHoursText] = useState(row.weekdayHours == null ? "" : String(row.weekdayHours));
  const [percentText, setPercentText] = useState(
    row.percentAvailable == null ? "100" : String(row.percentAvailable),
  );

  useEffect(() => {
    setHoursText(row.weekdayHours == null ? "" : String(row.weekdayHours));
    setPercentText(row.percentAvailable == null ? "100" : String(row.percentAvailable));
  }, [row.id, row.weekdayHours, row.percentAvailable]);

  const hours = Number(hoursText);
  const percent = Number(percentText);
  const hoursValid = Number.isFinite(hours) && hours >= 0 && hours <= 24;
  const percentValid = Number.isFinite(percent) && percent >= 0 && percent <= 100;
  const savedHours = row.weekdayHours == null ? "" : String(row.weekdayHours);
  const savedPercent = row.percentAvailable == null ? "100" : String(row.percentAvailable);
  const dirty = hoursText !== savedHours || percentText !== savedPercent;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <Label htmlFor={`roster-hours-${String(row.id)}`}>Hours each weekday</Label>
        <Input
          id={`roster-hours-${String(row.id)}`}
          className="mt-1 w-28"
          type="number"
          min={0}
          max={24}
          step={0.5}
          value={hoursText}
          onChange={(event) => setHoursText(event.target.value)}
          disabled={disabled}
        />
        {row.weekdayHours == null && hoursText === "" ? (
          <p className="mt-1 text-xs text-slate-500">No Monday-Friday schedule yet.</p>
        ) : null}
      </div>
      <div>
        <Label htmlFor={`roster-percent-${String(row.id)}`}>Available for production</Label>
        <Input
          id={`roster-percent-${String(row.id)}`}
          className="mt-1 w-28"
          type="number"
          min={0}
          max={100}
          step={1}
          value={percentText}
          onChange={(event) => setPercentText(event.target.value)}
          disabled={disabled}
        />
      </div>
      <HubSaveButton
        dirty={dirty && hoursValid && percentValid}
        disabled={disabled || !hoursValid || !percentValid}
        blockedHint={
          !hoursValid || !percentValid
            ? "Enter weekday hours from 0 to 24 and a production percent from 0 to 100."
            : undefined
        }
        isLoading={pending}
        onClick={() => onSave(row.id, hours, percent)}
      >
        Save
      </HubSaveButton>
    </div>
  );
}

export function ProductionCapacitySettingsSection() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubCapacitySettingsAllowed = useHubModuleAdmin("manufacturing_management");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [sunMinutesByWeekday, setSunMinutesByWeekday] = useState<Record<string, string>>({
    mon: "0",
    tue: "0",
    wed: "0",
    thu: "0",
    fri: "0",
    sat: "0",
    sun: "0",
  });
  const [newLocationId, setNewLocationId] = useState("1");
  const [newProductFamily, setNewProductFamily] = useState("fi_3c");
  const [newStationKey, setNewStationKey] = useState("config");
  const [newManMinutes, setNewManMinutes] = useState("10");

  const staffQuery = useQuery({
    queryKey: ["manufacturing", "production-capacity", "staff"],
    queryFn: () =>
      apiFetch<{ staff: StaffRow[] }>("/api/manufacturing/production-capacity/staff"),
  });
  const usersQuery = useQuery({
    queryKey: ["manufacturing", "production-capacity", "staff-users"],
    queryFn: () =>
      apiFetch<{ users: UserOption[] }>("/api/manufacturing/production-capacity/staff/user-options"),
  });
  const stationStandardsQuery = useQuery({
    queryKey: ["manufacturing", "production-capacity", "station-standards"],
    queryFn: () =>
      apiFetch<StationStandardsResponse>("/api/manufacturing/production-capacity/station-standards"),
  });
  const siteAllotmentsQuery = useQuery({
    queryKey: ["manufacturing", "production-capacity", "site-labor-allotments"],
    queryFn: () =>
      apiFetch<{ allotments: SiteAllotmentRow[] }>(
        "/api/manufacturing/production-capacity/site-labor-allotments",
      ),
  });

  const sunAllotment = siteAllotmentsQuery.data?.allotments.find(
    (row) => row.locationId === SUN_LOCATION_ID,
  );

  useEffect(() => {
    if (sunAllotment == null) return;
    setSunMinutesByWeekday({
      mon: String(sunAllotment.minutesByWeekday.mon ?? 0),
      tue: String(sunAllotment.minutesByWeekday.tue ?? 0),
      wed: String(sunAllotment.minutesByWeekday.wed ?? 0),
      thu: String(sunAllotment.minutesByWeekday.thu ?? 0),
      fri: String(sunAllotment.minutesByWeekday.fri ?? 0),
      sat: String(sunAllotment.minutesByWeekday.sat ?? 0),
      sun: String(sunAllotment.minutesByWeekday.sun ?? 0),
    });
  }, [sunAllotment]);

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "production-capacity"] });
  };

  const addStaffMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/manufacturing/production-capacity/staff", {
        method: "POST",
        body: JSON.stringify({
          userId: selectedUserId,
          locationId: SGS_LOCATION_ID,
          displayName: users.find((user) => user.userId === selectedUserId)?.displayName ?? null,
        }),
      }),
    onSuccess: () => {
      setSelectedUserId("");
      showToast({ message: "Added to the SGS roster at 8 weekday hours and 100 percent available.", tone: "success" });
      invalidateAll();
    },
    onError: (error) => showToast({ message: rosterErrorMessage(error), tone: "error" }),
  });

  const saveRosterMutation = useMutation({
    mutationFn: async ({
      staffId,
      weekdayHours,
      percentAvailable,
    }: {
      staffId: number;
      weekdayHours: number;
      percentAvailable: number;
    }) => {
      const effectiveFrom = chicagoToday();
      await apiFetch(`/api/manufacturing/production-capacity/staff/${String(staffId)}/schedule`, {
        method: "POST",
        body: JSON.stringify({
          effectiveFrom,
          hoursByWeekday: {
            mon: weekdayHours,
            tue: weekdayHours,
            wed: weekdayHours,
            thu: weekdayHours,
            fri: weekdayHours,
            sat: 0,
            sun: 0,
          },
        }),
      });
      await apiFetch(`/api/manufacturing/production-capacity/staff/${String(staffId)}/availability`, {
        method: "POST",
        body: JSON.stringify({
          effectiveFrom,
          percentAvailable,
        }),
      });
    },
    onSuccess: invalidateAll,
    onError: (error) => showToast({ message: rosterErrorMessage(error), tone: "error" }),
  });

  const removeStaffMutation = useMutation({
    mutationFn: (staffId: number) =>
      apiFetch(`/api/manufacturing/production-capacity/staff/${String(staffId)}`, {
        method: "DELETE",
      }),
    onSuccess: invalidateAll,
    onError: (error) => showToast({ message: rosterErrorMessage(error), tone: "error" }),
  });

  const saveStationStandardMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/manufacturing/production-capacity/station-standards", {
        method: "POST",
        body: JSON.stringify({
          locationId: Number(newLocationId),
          productFamily: newProductFamily,
          stationKey: newStationKey,
          manMinutesPerUnit: Number(newManMinutes),
        }),
      }),
    onSuccess: invalidateAll,
  });

  const refreshStationStandardsMutation = useMutation({
    mutationFn: () =>
      apiFetch<RefreshStandardsResponse>(
        "/api/manufacturing/production-capacity/station-standards/refresh-from-labor",
        { method: "POST" },
      ),
    onSuccess: invalidateAll,
  });

  const saveSunAllotmentMutation = useMutation({
    mutationFn: () =>
      apiFetch("/api/manufacturing/production-capacity/site-labor-allotments", {
        method: "PUT",
        body: JSON.stringify({
          locationId: SUN_LOCATION_ID,
          minutesByWeekday: {
            mon: Number(sunMinutesByWeekday.mon) || 0,
            tue: Number(sunMinutesByWeekday.tue) || 0,
            wed: Number(sunMinutesByWeekday.wed) || 0,
            thu: Number(sunMinutesByWeekday.thu) || 0,
            fri: Number(sunMinutesByWeekday.fri) || 0,
            sat: 0,
            sun: 0,
          },
        }),
      }),
    onSuccess: invalidateAll,
  });

  const deleteStationStandardMutation = useMutation({
    mutationFn: (standardId: number) =>
      apiFetch(`/api/manufacturing/production-capacity/station-standards/${String(standardId)}`, {
        method: "DELETE",
      }),
    onSuccess: invalidateAll,
  });

  const staff = (staffQuery.data?.staff ?? []).filter((row) => row.locationId === SGS_LOCATION_ID);
  const rosterUserIds = new Set(staff.map((row) => row.userId));
  const users = (usersQuery.data?.users ?? []).filter((user) => !rosterUserIds.has(user.userId));
  const stationStandards = stationStandardsQuery.data?.standards ?? [];
  const families = (stationStandardsQuery.data?.families ?? Object.keys(PRODUCT_FAMILY_LABELS)).filter(
    (family) =>
      newLocationId === "2"
        ? !["assembly_magnet", "clamp_qc"].includes(family)
        : true,
  );

  const sortedStationStandards = useMemo(
    () =>
      [...stationStandards].sort((left, right) => {
        if (left.locationId !== right.locationId) return left.locationId - right.locationId;
        const familyCompare = left.productFamily.localeCompare(right.productFamily);
        if (familyCompare !== 0) return familyCompare;
        return left.stationKey.localeCompare(right.stationKey);
      }),
    [stationStandards],
  );

  const stationKeyOptions = useMemo(
    () => stationKeysForFamilyEditor(newLocationId, newProductFamily),
    [newLocationId, newProductFamily],
  );

  useEffect(() => {
    if (!stationKeyOptions.includes(newStationKey) && stationKeyOptions.length > 0) {
      setNewStationKey(stationKeyOptions[0] ?? "config");
    }
  }, [newLocationId, newStationKey, stationKeyOptions]);

  const controlsDisabled =
    !hubCapacitySettingsAllowed ||
    staffQuery.isLoading ||
    usersQuery.isLoading ||
    addStaffMutation.isPending;

  return (
    <HubSettingsSection
      title="Production capacity"
      hoverHint={SECTION_HELP}
      collapsible
      defaultCollapsed
      sectionId="production-capacity"
    >
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>SGS labor roster</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm text-slate-600">
              Hours are the same Monday through Friday. Available for production is the percent of those hours used
              for builds. A new person starts at 8 hours and 100 percent.
            </p>
            <ReadOnlyHoverLayer
              active={!hubCapacitySettingsAllowed}
              hoverHint={capacitySettingsAdminTitle}
              layout="block"
            >
              <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                <div>
                  <Label htmlFor="capacity-staff-user">Hub user</Label>
                  <select
                    id="capacity-staff-user"
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={selectedUserId}
                    onChange={(event) => setSelectedUserId(event.target.value)}
                    disabled={controlsDisabled}
                  >
                    <option value="">Select user…</option>
                    {users.map((user) => (
                      <option key={user.userId} value={user.userId}>
                        {user.displayName ?? user.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={() => addStaffMutation.mutate()}
                    disabled={selectedUserId === "" || controlsDisabled}
                  >
                    Add to SGS roster
                  </Button>
                </div>
              </div>
            </ReadOnlyHoverLayer>

            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Active</th>
                  <th className="py-2 pr-4">Schedule</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {staff.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4 align-top">{row.displayName ?? row.userEmail ?? row.userId}</td>
                    <td className="py-2 pr-4 align-top">{row.active ? "Yes" : "No"}</td>
                    <td className="py-2 pr-4">
                      <ReadOnlyHoverLayer
                        active={!hubCapacitySettingsAllowed}
                        hoverHint={capacitySettingsAdminTitle}
                        layout="inline"
                      >
                        <RosterScheduleEditor
                          row={row}
                          disabled={!hubCapacitySettingsAllowed}
                          pending={saveRosterMutation.isPending && saveRosterMutation.variables?.staffId === row.id}
                          onSave={(staffId, weekdayHours, percentAvailable) =>
                            saveRosterMutation.mutate({ staffId, weekdayHours, percentAvailable })
                          }
                        />
                      </ReadOnlyHoverLayer>
                    </td>
                    <td className="py-2 align-top text-right">
                      <ReadOnlyHoverLayer
                        active={!hubCapacitySettingsAllowed}
                        hoverHint={capacitySettingsAdminTitle}
                        layout="inline"
                      >
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => removeStaffMutation.mutate(row.id)}
                          disabled={!hubCapacitySettingsAllowed || removeStaffMutation.isPending}
                        >
                          Remove
                        </Button>
                      </ReadOnlyHoverLayer>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Suntronic daily labor allotment</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm text-slate-600">
              Production man-minutes budget per weekday at Suntronic. Enter total minutes available for builds each day
              — individual staff are not tracked at this site.
            </p>
            <ReadOnlyHoverLayer
              active={!hubCapacitySettingsAllowed}
              hoverHint={capacitySettingsAdminTitle}
              layout="block"
            >
              <div className="grid gap-3 sm:grid-cols-5">
                {WEEKDAY_ALLOTMENT_KEYS.map((weekday) => (
                  <div key={weekday}>
                    <Label htmlFor={`sun-minutes-${weekday}`}>{weekday.toUpperCase()}</Label>
                    <Input
                      id={`sun-minutes-${weekday}`}
                      className="mt-1"
                      type="number"
                      min={0}
                      step={1}
                      value={sunMinutesByWeekday[weekday]}
                      onChange={(event) =>
                        setSunMinutesByWeekday((current) => ({
                          ...current,
                          [weekday]: event.target.value,
                        }))
                      }
                      disabled={!hubCapacitySettingsAllowed || siteAllotmentsQuery.isLoading}
                    />
                  </div>
                ))}
              </div>
              <Button
                type="button"
                className="mt-4"
                onClick={() => saveSunAllotmentMutation.mutate()}
                disabled={!hubCapacitySettingsAllowed || saveSunAllotmentMutation.isPending}
              >
                Save Suntronic allotment
              </Button>
            </ReadOnlyHoverLayer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Station man-minute standards</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm text-slate-600">
              Labor minutes per order unit by product family and station. SGS device families use the full hub
              manufacturing chain; Suntronic uses before/after configuration buckets. Use refresh to seed SGS
              standards from the last three weeks of labor scans.
            </p>
            <ReadOnlyHoverLayer
              active={!hubCapacitySettingsAllowed}
              hoverHint={capacitySettingsAdminTitle}
              layout="block"
            >
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => refreshStationStandardsMutation.mutate()}
                  disabled={!hubCapacitySettingsAllowed || refreshStationStandardsMutation.isPending}
                >
                  Build from last 3 weeks (SGS)
                </Button>
                {refreshStationStandardsMutation.data != null ? (
                  <span className="self-center text-sm text-slate-600">
                    Updated {String(refreshStationStandardsMutation.data.updated)} rows
                    {refreshStationStandardsMutation.data.skipped.length > 0
                      ? `; ${String(refreshStationStandardsMutation.data.skipped.length)} skipped (low sample)`
                      : ""}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-5">
                <div>
                  <Label htmlFor="capacity-standard-location">Site</Label>
                  <select
                    id="capacity-standard-location"
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={newLocationId}
                    onChange={(event) => setNewLocationId(event.target.value)}
                    disabled={!hubCapacitySettingsAllowed}
                  >
                    <option value="1">SGS</option>
                    <option value="2">Suntronic</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="capacity-standard-family">Product family</Label>
                  <select
                    id="capacity-standard-family"
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={newProductFamily}
                    onChange={(event) => setNewProductFamily(event.target.value)}
                    disabled={!hubCapacitySettingsAllowed}
                  >
                    {families.map((family) => (
                      <option key={family} value={family}>
                        {PRODUCT_FAMILY_LABELS[family] ?? family}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="capacity-standard-station">Station</Label>
                  <select
                    id="capacity-standard-station"
                    className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                    value={newStationKey}
                    onChange={(event) => setNewStationKey(event.target.value)}
                    disabled={!hubCapacitySettingsAllowed}
                  >
                    {stationKeyOptions.map((stationKey) => (
                      <option key={stationKey} value={stationKey}>
                        {STATION_LABELS[stationKey] ?? stationKey}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="capacity-standard-minutes">Man-minutes / unit</Label>
                  <Input
                    id="capacity-standard-minutes"
                    className="mt-1"
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={newManMinutes}
                    onChange={(event) => setNewManMinutes(event.target.value)}
                    disabled={!hubCapacitySettingsAllowed}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={() => saveStationStandardMutation.mutate()}
                    disabled={!hubCapacitySettingsAllowed || saveStationStandardMutation.isPending}
                  >
                    Save standard
                  </Button>
                </div>
              </div>
            </ReadOnlyHoverLayer>

            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="py-2 pr-4">Site</th>
                  <th className="py-2 pr-4">Family</th>
                  <th className="py-2 pr-4">Station</th>
                  <th className="py-2 pr-4 text-right">Min / unit</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {sortedStationStandards.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-slate-600">
                      No station standards saved yet. Add rows manually or use Build from last 3 weeks for SGS device
                      families.
                    </td>
                  </tr>
                ) : (
                  sortedStationStandards.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="py-2 pr-4">{row.locationName ?? row.locationId}</td>
                    <td className="py-2 pr-4">{PRODUCT_FAMILY_LABELS[row.productFamily] ?? row.productFamily}</td>
                    <td className="py-2 pr-4">{STATION_LABELS[row.stationKey] ?? row.stationKey}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {formatNumber(row.manMinutesPerUnit)}
                    </td>
                    <td className="py-2 text-right">
                      <ReadOnlyHoverLayer
                        active={!hubCapacitySettingsAllowed}
                        hoverHint={capacitySettingsAdminTitle}
                        layout="inline"
                      >
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => deleteStationStandardMutation.mutate(row.id)}
                          disabled={!hubCapacitySettingsAllowed || deleteStationStandardMutation.isPending}
                        >
                          Remove
                        </Button>
                      </ReadOnlyHoverLayer>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </HubSettingsSection>
  );
}
